import React, { useEffect, useMemo, useState } from "react";

import { useCreateAlertRule, useGetAllTriggers, useUpdateAlertRule } from "api/api-hooks/alert-rules-hooks";
import { useGetCompanies } from "api/api-hooks/company-hooks";
import { useGetCompanyUsers } from "api/api-hooks/user-hooks";
import {
  ALERT_PRIORITY_OPTIONS,
  ALERT_TYPE_OPTIONS,
  AlertRuleDTO,
  AlertTargetType,
  CreateAlertRuleRequest,
  CreateAlertTriggerRequest,
  TARGET_TYPE_OPTIONS,
  TargetSettings,
  TRIGGER_LOGIC_OPTIONS,
  TriggerSettings,
  UpdateAlertRuleRequest,
} from "api/model/alerts/alert-types";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { useHasPermissions } from "hooks/useHasPermissions";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { T } from "services/lang/LanguageService";
import TriggersSection from "./TriggersSection";
import { TriggerData, TriggerFormItem } from "./types";
import { getDefaultTypeSettings } from "./utils";

const buildTargetSettings = (
  targetType: AlertTargetType,
  settings?: Record<string, unknown>
): TargetSettings => {
  switch (targetType) {
    case "COMPANY":
      return { targetType: "COMPANY", companyRef: (settings as { companyRef?: string })?.companyRef ?? "" };
    case "USER":
      return { targetType: "USER", userId: (settings as { userId?: number })?.userId ?? 0 };
    case "GLOBAL":
    default:
      return { targetType: "GLOBAL" };
  }
};

type AlertRuleFormProps = {
  rule?: AlertRuleDTO;
  onClose: () => void;
};

const AlertRuleForm: React.FC<AlertRuleFormProps> = ({ rule, onClose }) => {
  const isEdit = !!rule;
  const { mutate: createRule, isPending: isCreating } = useCreateAlertRule({ onSuccess: onClose });
  const { mutate: updateRule, isPending: isUpdating } = useUpdateAlertRule({ onSuccess: onClose });
  const { data: availableTriggers } = useGetAllTriggers();

  const isAdmin = useHasPermissions("ADMIN");
  const company = useCurrentCompanyContext();
  const { data: companyUsers } = useGetCompanyUsers(company?.companyReference ?? "", {
    enabled: !!company?.companyReference,
  });
  const { data: allCompanies } = useGetCompanies({ enabled: isAdmin });

  const filteredAlertTypeOptions = useMemo(
    () => (isAdmin ? ALERT_TYPE_OPTIONS : ALERT_TYPE_OPTIONS.filter(opt => opt.value !== "SERVER_MESSAGE")),
    [isAdmin]
  );

  const filteredTargetTypeOptions = useMemo(
    () => (isAdmin ? TARGET_TYPE_OPTIONS : TARGET_TYPE_OPTIONS.filter(opt => opt.value !== "GLOBAL")),
    [isAdmin]
  );

  const [formData, setFormData] = useState({
    name: rule?.name ?? "",
    enabled: rule?.enabled ?? true,
    alertType: rule?.alertType ?? ("SERVER_MESSAGE" as const),
    priority: rule?.priority ?? ("WARNING" as const),
    message: rule?.message ?? "",
    shortMessage: rule?.shortMessage ?? "",
    timeoutMinutes: rule?.timeoutMinutes ?? 60,
    targetType: rule?.targetType ?? ("GLOBAL" as const),
    targetSettings: rule?.targetSettings ?? undefined,
    triggerLogic: rule?.triggerLogic ?? ("ANY" as const),
  });

  // Adjust default values for non-admin users when creating new rules
  useEffect(() => {
    if (!rule && !isAdmin) {
      setFormData(prev => ({
        ...prev,
        alertType: prev.alertType === "SERVER_MESSAGE" ? "TEXT" : prev.alertType,
        targetType: prev.targetType === "GLOBAL" ? "COMPANY" : prev.targetType,
      }));
    }
  }, [isAdmin, rule]);

  // Auto-set company reference for non-admin users when target is COMPANY
  useEffect(() => {
    if (!isAdmin && formData.targetType === "COMPANY" && company?.companyReference) {
      setFormData(prev => ({
        ...prev,
        targetSettings: { companyRef: company.companyReference },
      }));
    }
  }, [isAdmin, formData.targetType, company?.companyReference]);

  // Reset typeSettings when alertType changes
  useEffect(() => {
    if (!rule) {
      setFormData(prev => ({
        ...prev,
        typeSettings: getDefaultTypeSettings(prev.alertType),
      }));
    }
  }, [formData.alertType, rule]);

  // Track triggers separately
  const [triggerItems, setTriggerItems] = useState<TriggerFormItem[]>(() => {
    if (!rule?.triggers) return [];
    return rule.triggers.map(t => ({
      isExisting: true,
      existingId: t.id,
      existingTrigger: t,
    }));
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const triggers: CreateAlertTriggerRequest[] = triggerItems.map(item => {
      if (item.isExisting && item.existingId && item.isEdited && item.editedData) {
        return {
          id: item.existingId,
          name: item.editedData.name,
          companyRef: company?.companyReference,
          settings: {
            ...item.editedData.settings,
            triggerType: item.editedData.triggerType,
          } as TriggerSettings,
          retentionTime: item.editedData.retentionTime,
        };
      }
      if (item.isExisting && item.existingId) {
        return { id: item.existingId };
      }
      if (item.newTrigger) {
        return {
          name: item.newTrigger.name,
          companyRef: company?.companyReference,
          settings: {
            ...item.newTrigger.settings,
            triggerType: item.newTrigger.triggerType,
          } as TriggerSettings,
          retentionTime: item.newTrigger.retentionTime,
        };
      }
      return {};
    });

    const { targetType, targetSettings, ...rest } = formData;
    const request: CreateAlertRuleRequest = {
      ...rest,
      targetSettings: buildTargetSettings(targetType, targetSettings),
      companyRef: company?.companyReference ?? "",
      triggers,
    };

    if (isEdit && rule) {
      updateRule({ ...request, id: rule.id } as UpdateAlertRuleRequest);
    } else {
      createRule(request);
    }
  };

  const updateField = <K extends keyof typeof formData>(field: K, value: (typeof formData)[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTargetTypeChange = (value: typeof formData.targetType) => {
    updateField("targetType", value);
    if (value === "GLOBAL") {
      updateField("targetSettings", undefined);
    } else if (value === "COMPANY" && !isAdmin && company?.companyReference) {
      updateField("targetSettings", { companyRef: company.companyReference });
    } else if (value !== "USER" && value !== "COMPANY") {
      updateField("targetSettings", undefined);
    }
  };

  // Trigger handlers
  const handleAddExistingTrigger = (triggerId: number) => {
    const trigger = availableTriggers?.find(t => t.id === triggerId);
    if (!trigger) return;
    setTriggerItems(prev => [...prev, { isExisting: true, existingId: triggerId, existingTrigger: trigger }]);
  };

  const handleAddNewTrigger = (trigger: TriggerData) => {
    setTriggerItems(prev => [...prev, { isExisting: false, newTrigger: trigger }]);
  };

  const handleEditTrigger = (index: number, data: TriggerData) => {
    setTriggerItems(prev =>
      prev.map((item, i) => (i === index ? { ...item, isEdited: true, editedData: data } : item))
    );
  };

  const handleRemoveTrigger = (index: number) => {
    setTriggerItems(prev => prev.filter((_, i) => i !== index));
  };

  const isPending = isCreating || isUpdating;

  return (
    <form onSubmit={handleSubmit} className="flex flex-column gap-3">
      {/* Basic Info */}
      <div className="grid">
        <div className="col-9">
          <label htmlFor="name" className="block mb-1 font-semibold">
            {T("word.name")} *
          </label>
          <InputText
            id="name"
            value={formData.name}
            onChange={e => updateField("name", e.target.value)}
            className="w-full"
            required
          />
        </div>
        <div className="col-3 flex align-items-center gap-2">
          <InputSwitch checked={formData.enabled} onChange={e => updateField("enabled", e.value)} />
          <label>{T("word.enabled")}</label>
        </div>

        <div className="col-6">
          <label htmlFor="alertType" className="block mb-1 font-semibold">
            {T("word.type")} *
          </label>
          <Dropdown
            id="alertType"
            value={formData.alertType}
            options={filteredAlertTypeOptions}
            onChange={e => updateField("alertType", e.value)}
            className="w-full"
          />
        </div>

        <div className="col-6">
          <label htmlFor="priority" className="block mb-1 font-semibold">
            {T("word.priority")} *
          </label>
          <Dropdown
            id="priority"
            value={formData.priority}
            options={ALERT_PRIORITY_OPTIONS}
            onChange={e => updateField("priority", e.value)}
            className="w-full"
          />
        </div>

        <div className="col-6">
          <label htmlFor="targetType" className="block mb-1 font-semibold">
            {T("word.target")} *
          </label>
          <Dropdown
            id="targetType"
            value={formData.targetType}
            options={filteredTargetTypeOptions}
            onChange={e => handleTargetTypeChange(e.value)}
            className="w-full"
          />
        </div>

        <div className="col-6">
          <label htmlFor="triggerLogic" className="block mb-1 font-semibold">
            {T("alerts.trigger-logic")} *
          </label>
          <Dropdown
            id="triggerLogic"
            value={formData.triggerLogic}
            options={TRIGGER_LOGIC_OPTIONS}
            onChange={e => updateField("triggerLogic", e.value)}
            className="w-full"
          />
        </div>

        {formData.targetType === "COMPANY" && isAdmin && (
          <div className="col-6">
            <label htmlFor="targetCompany" className="block mb-1 font-semibold">
              {T("word.company")} *
            </label>
            <Dropdown
              id="targetCompany"
              value={(formData.targetSettings as { companyRef?: string })?.companyRef}
              options={allCompanies ?? []}
              optionLabel="title"
              optionValue="companyReference"
              onChange={e => updateField("targetSettings", { companyRef: e.value })}
              className="w-full"
              filter
              placeholder={T("action.select")}
            />
          </div>
        )}

        {formData.targetType === "USER" && (
          <div className="col-6">
            <label htmlFor="targetUser" className="block mb-1 font-semibold">
              {T("word.user")} *
            </label>
            <Dropdown
              id="targetUser"
              value={(formData.targetSettings as { userId?: number })?.userId}
              options={companyUsers ?? []}
              optionLabel="displayName"
              optionValue="id"
              onChange={e => updateField("targetSettings", { userId: e.value })}
              className="w-full"
              filter
              placeholder={T("action.select")}
            />
          </div>
        )}

        <div className="col-12 md:col-6">
          <label htmlFor="timeoutMinutes" className="block mb-1 font-semibold">
            {`${T("word.timeout")} (${T("time.minutes")})`}
          </label>
          <InputNumber
            id="timeoutMinutes"
            value={(formData?.timeoutMinutes as number) ?? 60}
            onValueChange={e => updateField("timeoutMinutes", e.value ?? 60)}
            className="w-full"
            min={1}
            suffix=" min"
          />
        </div>
        <div className="col-12">
          <label htmlFor="shortMessage" className="block mb-1 font-semibold">
            {T("label.shorten-text")}
          </label>
          <InputText
            id="shortMessage"
            value={(formData?.shortMessage as string) ?? ""}
            onChange={e => updateField("shortMessage", e.target.value)}
            className="w-full"
            placeholder={T("word.brief-message-for-notifications")}
          />
        </div>

        <div className="col-12">
          <label htmlFor="message" className="block mb-1 font-semibold">
            {`${T("word.message")} (HTML)`}
          </label>
          <InputTextarea
            id="message"
            value={(formData?.message as string) ?? ""}
            onChange={e => updateField("message", e.target.value)}
            className="w-full"
            rows={4}
          />
        </div>
      </div>

      {/* Triggers Section */}
      <TriggersSection
        triggerItems={triggerItems}
        availableTriggers={availableTriggers}
        onAddExisting={handleAddExistingTrigger}
        onAddNew={handleAddNewTrigger}
        onEdit={handleEditTrigger}
        onRemove={handleRemoveTrigger}
      />

      {/* Form Actions */}
      <div className="flex justify-content-end gap-2">
        <Button
          type="button"
          label={T("word.cancel")}
          onClick={onClose}
          className="p-button-secondary p-button-outlined"
        />
        <Button
          type="submit"
          label={isEdit ? T("action.save") : T("action.create")}
          loading={isPending}
          disabled={!formData.name}
        />
      </div>
    </form>
  );
};

export default AlertRuleForm;
